import {
    ProcessFieldType,
    ServiceProcessFieldTableColumnVM,
} from '@shared/types/process';
import { Lng, LngRecord } from '@shared/types/types';
import { getLangText } from '@shared/util/textGetters';

import { normalizeColumnOptions } from './tableFieldColumnUtils';
import {
    getColumnTitle,
    GLOBAL_SEARCH_FILTER_KEY,
    TableRowsQueryState,
} from './tableFieldUtils';

import { TabulatorFilterConfig } from '@shadcn-features/core/ShadcnTabulator/components/filters.config';

type ColumnSelectOption = {
    id?: string;
    value?: string;
    label?: LngRecord | string;
};

type ParsedColumnOptions = {
    options?: ColumnSelectOption[];
    mode?: 'multiple' | 'single';
};

const TEXT_FILTER_TYPES = new Set<ProcessFieldType>([
    'String',
    'Text',
    'Int',
    'Float',
]);

function parseColumnOptions(
    column: ServiceProcessFieldTableColumnVM,
): ParsedColumnOptions | null {
    const parsed = normalizeColumnOptions(column.options);
    if (parsed == null) {
        return null;
    }

    return parsed as ParsedColumnOptions;
}

function mapSelectOptions(
    column: ServiceProcessFieldTableColumnVM,
    lang: Lng,
): TabulatorFilterConfig['options'] {
    const parsed = parseColumnOptions(column);
    const items = parsed?.options ?? [];

    return items.map((item) => {
        const value = item.id ?? item.value ?? '';
        const label =
            typeof item.label === 'string'
                ? item.label
                : getLangText(item.label, lang) || value;

        return { value, label };
    });
}

function isMultipleSelectColumn(
    column: ServiceProcessFieldTableColumnVM,
): boolean {
    const parsed = parseColumnOptions(column);
    return parsed?.mode === 'multiple';
}

function resolveColumnFilterType(
    column: ServiceProcessFieldTableColumnVM,
): TabulatorFilterConfig['type'] {
    if (column.fillSourceId === 'SourceField') {
        return 'input';
    }

    const typeId = column.manualFieldTypeId;

    if (!typeId) {
        return 'input';
    }

    if (typeId === 'Date' || typeId === 'DateRange') {
        return 'dateRange';
    }

    if (typeId === 'Checkbox') {
        return 'select';
    }

    if (typeId === 'Select' || typeId === 'SelectIdp') {
        return isMultipleSelectColumn(column) ? 'multiSelect' : 'select';
    }

    if (TEXT_FILTER_TYPES.has(typeId)) {
        return 'input';
    }

    return 'input';
}

function resolveColumnFilterOptions(
    column: ServiceProcessFieldTableColumnVM,
    lang: Lng,
    type: TabulatorFilterConfig['type'],
): TabulatorFilterConfig['options'] {
    if (type === 'select' && column.manualFieldTypeId === 'Checkbox') {
        return [
            { label: 'true', value: 'true' },
            { label: 'false', value: 'false' },
        ];
    }

    if (type === 'select' || type === 'multiSelect') {
        return mapSelectOptions(column, lang);
    }

    return undefined;
}

export function buildTableFieldFilters(
    columns: ServiceProcessFieldTableColumnVM[],
    lang: Lng,
    globalSearchLabel: string,
): TabulatorFilterConfig[] {
    const globalSearchFilter: TabulatorFilterConfig = {
        key: GLOBAL_SEARCH_FILTER_KEY,
        label: globalSearchLabel,
        dataIndex: GLOBAL_SEARCH_FILTER_KEY,
        type: 'input',
        mainFilter: true,
    };

    const columnFilters = columns.map((column, index) => {
        const type = resolveColumnFilterType(column);

        return {
            key: column.id,
            label: getColumnTitle(column, lang) || column.id,
            dataIndex: column.id,
            type,
            options: resolveColumnFilterOptions(column, lang, type),
            pinned: index < 3,
        };
    });

    return [globalSearchFilter, ...columnFilters];
}

export function syncParamsToQueryState(
    params: Record<string, unknown>,
    columnIds: string[],
): Partial<TableRowsQueryState> {
    const searchRaw = params[GLOBAL_SEARCH_FILTER_KEY];
    const search =
        typeof searchRaw === 'string' && searchRaw.trim() !== ''
            ? searchRaw
            : undefined;

    const columnFilters: Record<string, unknown> = {};

    columnIds.forEach((columnId) => {
        const value = params[columnId];
        if (value == null || value === '') {
            return;
        }

        if (Array.isArray(value) && value.length === 0) {
            return;
        }

        if (typeof value === 'string' && value.trim() === '') {
            return;
        }

        columnFilters[columnId] = value;
    });

    return {
        search,
        columnFilters,
        page: 1,
    };
}

export function queryStateToParams(
    queryState: TableRowsQueryState,
    columnIds: string[],
): Record<string, unknown> {
    const params: Record<string, unknown> = {};

    if (queryState.search) {
        params[GLOBAL_SEARCH_FILTER_KEY] = queryState.search;
    }

    columnIds.forEach((columnId) => {
        const value = queryState.columnFilters?.[columnId];
        if (value != null && value !== '') {
            params[columnId] = value;
        }
    });

    return params;
}
